import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'

import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'

import RouteViewComponent from '../layouts/RouterBypass.vue'

const routes: Array<RouteRecordRaw> = [
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'login' },
  },
  {
    name: 'admin',
    path: '/',
    component: AppLayout,
    redirect: { name: 'login' },
    children: [
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/settings/Settings.vue'),
      },
      {
        name: 'preferences',
        path: 'preferences',
        component: () => import('../pages/preferences/Preferences.vue'),
      },
      {
        name: 'users',
        path: 'users',
        component: () => import('../pages/users/UsersPage.vue'),
      },
      {
        name: 'map',
        path: 'map',
        component: () => import('../pages/map/MapPage.vue'),
      },
      {
        name: 'devices',
        path: 'devices',
        component: () => import('../pages/devices/DevicesPage.vue'),
      },
      {
        name: 'device-detail',
        path: 'devices/:id',
        component: () => import('../pages/devices/DeviceDetailPage.vue'),
      },
      {
        name: 'DeviceErrorLogs',
        path: 'devices/:id/error-logs',
        component: () => import('../pages/devices/ErrorLogsPage.vue'),
      },
      {
        name: 'DeviceActivityLogs',
        path: 'devices/:id/activity-logs',
        component: () => import('../pages/devices/ActivityLogsPage.vue'),
      },
      {
        name: 'payments',
        path: '/payments',
        component: RouteViewComponent,
        children: [
          {
            name: 'payment-methods',
            path: 'payment-methods',
            component: () => import('../pages/payments/PaymentsPage.vue'),
          },
          {
            name: 'billing',
            path: 'billing',
            component: () => import('../pages/billing/BillingPage.vue'),
          },
          {
            name: 'pricing-plans',
            path: 'pricing-plans',
            component: () => import('../pages/pricing-plans/PricingPlans.vue'),
          },
        ],
      },
      {
        name: 'faq',
        path: '/faq',
        component: () => import('../pages/faq/FaqPage.vue'),
      },
    ],
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/Signup.vue'),
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

// Simple global guard: redirect to login if user is not authenticated.
// We import the store inside the guard to avoid circular import at module evaluation time.
router.beforeEach(async (to, from, next) => {
  // allow all auth pages and 404
  if (to.path.startsWith('/auth') || to.path === '/404') {
    return next()
  }
  // lazy import to avoid ESM cycles (use dynamic import in the browser)
  const authModule = await import('../stores/auth')
  const { useAuthStore } = authModule as typeof import('../stores/auth')
  const authStore = useAuthStore()
  if (!authStore.isAuthenticated()) {
    return next({ name: 'login' })
  }

  next()
})

export default router
